/**
 * 遮牌过渡页（§11.8，红线 #12）。
 *
 * 四条硬性约束，每一条都对应一种真实的走光方式：
 *
 * 1. **不透明纯色**。背景由父层 `.veil` 的 `background: var(--c-veil)` 提供，
 *    不用 `opacity`、不用 `backdrop-filter` —— 半透明和模糊都能被截图还原，
 *    慢速渲染的设备上还会有几帧真的能看穿。
 * 2. **进入零动画**。本组件没有任何入场 transition/animation；`veiled` 一置真就是最终态。
 *    淡入过程中只要有一帧能看见底牌，遮牌就等于没做。
 * 3. **底牌不在 DOM 里**。本组件的 props 里**根本没有牌**这个概念 —— 这不是「不渲染」，
 *    是「拿不到」。同时 App 对 `.veil` 做的是条件挂载，牌桌那边由 `heroHoleCards()` 判空，
 *    两头都不是 `display:none` / `visibility:hidden`（那两种 DevTools 和读屏都能穿透）。
 * 4. **必须主动长按 500ms 才揭示**。点一下就开的话，递手机路上蹭一下就被旁边的人瞥见了。
 *
 * 另外显示「该交给谁」的名字（§11.8 第 8 条）：递错手机时对方能立刻察觉。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { Chips, SeatIndex } from '@poker/shared'

import { fireHaptic } from '../fx/haptics.js'
import { useSettingsStore, useTableStore, useUiStore } from '../store/index.js'
import type { VeilReason } from '../store/uiStore.js'
import { formatChips } from './format.js'
import { useLocalSessionStore } from './localSession.js'
import { LONG_PRESS_MS } from './usePeekGuard.js'

/** 进度环几何。半径写死是为了让 stroke-dasharray 能在模块级算好，动画期间只改一个数。 */
const RING_R = 46
const RING_C = 2 * Math.PI * RING_R

export interface PassDeviceScreenProps {
  /** 该拿手机的人。null / 空串时退化成「下一位」 */
  readonly targetName: string | null
  readonly targetSeat: SeatIndex | null
  readonly reason: VeilReason
  /** 公开信息，谁都能看，放这里是为了让等待的人知道牌局进度 */
  readonly potTotal: Chips
  readonly toCall: Chips
  readonly handNo: number
  /** 「张三 加注到 $200」 */
  readonly lastActionText?: string | null
  /** 手牌还没轮到任何人（比如正在结算）时置 false，长按区变成禁用态 */
  readonly canReveal?: boolean
  readonly holdMs?: number
  readonly haptics?: boolean
  readonly onReveal: () => void
}

/** 头像占位：取昵称首字。不做随机配色，避免同一个人每手换个颜色。 */
function initialOf(name: string): string {
  return name.trim().slice(0, 1) || '?'
}

function Line({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div style={{ display: 'grid', gap: 2, justifyItems: 'center' }}>
      <span style={{ fontSize: 'var(--fs-badge)', color: 'var(--c-text-mute)' }}>{label}</span>
      <span className="num" style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-bold)', color: 'var(--c-gold)' }}>
        {value}
      </span>
    </div>
  )
}

const HEADLINE: Readonly<Record<VeilReason, string>> = {
  init: '准备开始',
  pass: '把手机交给',
  blur: '已自动遮挡',
}

export function PassDeviceScreen(props: PassDeviceScreenProps): ReactNode {
  const holdMs = props.holdMs ?? LONG_PRESS_MS
  const canReveal = props.canReveal ?? true
  const name = props.targetName !== null && props.targetName.trim().length > 0 ? props.targetName : '下一位'

  const [pressing, setPressing] = useState(false)
  const ringRef = useRef<SVGCircleElement | null>(null)
  const rafRef = useRef(0)
  const startRef = useRef(0)
  const doneRef = useRef(false)

  // 红线 #48：进度环每帧直接写 DOM 属性，不走 React state。
  // 500ms 里 setState 三十次会把整个遮牌页重渲染三十遍，而这时手机正被递来递去。
  const paint = useCallback((ratio: number) => {
    const el = ringRef.current
    if (el === null) return
    el.setAttribute('stroke-dashoffset', String(RING_C * (1 - Math.min(1, Math.max(0, ratio)))))
  }, [])

  const stop = useCallback(() => {
    if (rafRef.current !== 0) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
    }
    setPressing(false)
    paint(0)
  }, [paint])

  const finish = useCallback(() => {
    if (doneRef.current) return
    doneRef.current = true
    // R4：能力检测 + 降级由 fireHaptic 负责；iOS 上直接调 vibrate 是静默失效，
    // 而「手牌已揭开」正是最不能没有反馈的一下。
    if (props.haptics === true) fireHaptic('select', true)
    stop()
    props.onReveal()
  }, [props, stop])

  const tick = useCallback(() => {
    const elapsed = performance.now() - startRef.current
    paint(elapsed / holdMs)
    if (elapsed >= holdMs) {
      finish()
      return
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [finish, holdMs, paint])

  const begin = useCallback(
    (e?: { currentTarget: unknown; pointerId?: number }) => {
      if (!canReveal || pressing || doneRef.current) return
      const target = e?.currentTarget
      if (target instanceof Element && e?.pointerId !== undefined) {
        try {
          target.setPointerCapture(e.pointerId)
        } catch {
          // 捕获失败只是少了「滑出仍能收到 pointerup」，不影响主流程
        }
      }
      setPressing(true)
      startRef.current = performance.now()
      rafRef.current = requestAnimationFrame(tick)
    },
    [canReveal, pressing, tick],
  )

  useEffect(() => () => {
    if (rafRef.current !== 0) cancelAnimationFrame(rafRef.current)
  }, [])

  const ringStyle: CSSProperties = {
    transform: 'rotate(-90deg)',
    transformOrigin: '50% 50%',
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        gap: 'var(--sp-4)',
        height: '100%',
        width: '100%',
        textAlign: 'center',
      }}
    >
      {/* 上：上一位做了什么 */}
      <div style={{ minHeight: 24, color: 'var(--c-text-dim)', fontSize: 'var(--fs-sm)' }}>
        {props.lastActionText ?? `第 ${props.handNo} 手`}
      </div>

      {/* 中：交给谁 + 长按区 */}
      <div style={{ display: 'grid', placeContent: 'center', gap: 'var(--sp-5)', justifyItems: 'center' }}>
        <div style={{ color: 'var(--c-text-dim)', fontSize: 'var(--fs-base)' }}>{HEADLINE[props.reason]}</div>
        <div
          style={{
            fontSize: 'var(--fs-2xl)',
            fontWeight: 'var(--fw-heavy)',
            color: 'var(--c-text)',
            lineHeight: 'var(--lh-tight)',
          }}
        >
          {name}
        </div>

        <button
          type="button"
          className="interactive"
          aria-label={`长按查看 ${name} 的手牌`}
          disabled={!canReveal}
          style={{
            position: 'relative',
            width: 128,
            height: 128,
            borderRadius: '50%',
            background: canReveal ? 'var(--c-surface-2)' : 'var(--c-btn-disabled)',
            border: '1px solid var(--c-line-strong)',
            color: 'var(--c-text)',
            display: 'grid',
            placeItems: 'center',
            // 长按必须由我们自己判定，交给系统会先弹出选择/放大镜
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
          }}
          onPointerDown={begin}
          onPointerUp={stop}
          onPointerCancel={stop}
          onPointerLeave={stop}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') begin()
          }}
          onKeyUp={stop}
          onContextMenu={(e) => e.preventDefault()}
        >
          <svg width={128} height={128} viewBox="0 0 128 128" style={ringStyle} aria-hidden="true">
            <circle cx="64" cy="64" r={RING_R} fill="none" stroke="var(--c-line)" strokeWidth="6" />
            <circle
              ref={ringRef}
              cx="64"
              cy="64"
              r={RING_R}
              fill="none"
              stroke="var(--c-turn)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C}
            />
          </svg>
          <span
            style={{
              position: 'absolute',
              fontSize: 'var(--fs-xl)',
              fontWeight: 'var(--fw-heavy)',
              color: canReveal ? 'var(--c-text)' : 'var(--c-btn-disabled-text)',
            }}
          >
            {initialOf(name)}
          </span>
        </button>

        <div style={{ fontSize: 'var(--fs-sm)', color: pressing ? 'var(--c-turn)' : 'var(--c-text-mute)' }}>
          {canReveal ? (pressing ? '按住不放…' : '长按查看手牌') : '等待发牌'}
        </div>
      </div>

      {/* 下：公开信息。底池与待跟注是全场可见的，放在遮牌页上不构成泄漏。 */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--sp-8)' }}>
        <Line label="底池" value={`$${formatChips(props.potTotal)}`} />
        <Line label="需跟注" value={props.toCall > 0 ? `$${formatChips(props.toCall)}` : '可过牌'} />
      </div>
    </div>
  )
}

/**
 * 接到 store 上的版本。直接塞进 `<App veil={<ConnectedPassDeviceScreen />} />`。
 *
 * 它读的全是公开字段（底池、待跟注、昵称）—— **没有任何一处取底牌**。
 * 这不是自觉，是结构：底牌只经 `heroHoleCards()` 出去，而那个函数在 `veiled` 为真时恒返回 null。
 */
export function ConnectedPassDeviceScreen(): ReactNode {
  const reason = useUiStore((s) => s.veilReason)
  const targetSeat = useUiStore((s) => s.veilTargetSeat)
  const potTotal = useTableStore((s) => s.view?.potTotal ?? 0)
  const toCall = useTableStore((s) => s.legal?.toCall ?? 0)
  const seats = useTableStore((s) => s.seats)
  const handNo = useLocalSessionStore((s) => s.handNo)
  const lastActionText = useLocalSessionStore((s) => s.lastActionText)
  const sessionName = useLocalSessionStore((s) => s.perspectiveName)
  const haptics = useSettingsStore((s) => s.haptics)
  const unveil = useUiStore((s) => s.unveil)

  const seatName = targetSeat === null ? null : (seats.find((x) => x.seat === targetSeat)?.name ?? null)

  return (
    <PassDeviceScreen
      targetName={seatName ?? sessionName}
      targetSeat={targetSeat}
      reason={reason}
      potTotal={potTotal}
      toCall={toCall}
      handNo={handNo}
      lastActionText={lastActionText}
      haptics={haptics}
      onReveal={unveil}
    />
  )
}

export default PassDeviceScreen
